/**
 * User Entity Definition
 */
import { z } from "zod";
import { CompanyRelationSchema, DiscountRelationSchema, SubscriptionTierRelationSchema, UserRelationSchema, } from "../schemas/relations";
import { PartnerInfoSchema, QuizResultSchema, TaxProfileSchema } from "../schemas/user.schemas";
import { BillingDataEntityDef } from "./billing-data.entity";
import { ChargeEntityDef, PaymentEntityDef, RefundEntityDef } from "./charge.entity";
import { defineEntity } from "./core";
import { ExchangeAccountEntityDef } from "./exchange-account.entity";
import { PaymentMethodEntityDef } from "./payment-method.entity";
import { PayoutEntityDef } from "./payout.entity";
import { ReferralCodeEntityDef, ReferralEntityDef } from "./referral.entity";
import { StrategyEntityDef } from "./strategy.entity";
import { SubscriptionEntityDef } from "./subscription.entity";
import { UserRoleEntityDef } from "./user-role.entity";
// SubscriptionTier entity
const SubscriptionTierEntityDef = defineEntity({
    name: "subscription-tier",
    schema: SubscriptionTierRelationSchema,
    relations: {},
});
// Company entity
const CompanyEntityDef = defineEntity({
    name: "company",
    schema: CompanyRelationSchema,
    relations: {},
});
// Discount entity
const DiscountEntityDef = defineEntity({
    name: "discount",
    schema: DiscountRelationSchema,
    relations: {},
});
// TaxProfile entity (component)
const TaxProfileEntityDef = defineEntity({
    name: "tax-profile",
    schema: TaxProfileSchema,
    relations: {},
});
// PartnerInfo entity (component)
const PartnerInfoEntityDef = defineEntity({
    name: "partner-info",
    schema: PartnerInfoSchema,
    relations: {},
});
// QuizResult entity (component)
const QuizResultEntityDef = defineEntity({
    name: "quiz-result",
    schema: QuizResultSchema,
    relations: {},
});
// Extra fields not in the relation schema
const UserExtraFieldsSchema = z.object({
    provider: z.string().nullish(),
    trustpilotInvitationSent: z.boolean().nullish(),
});
export const UserEntityDef = defineEntity({
    name: "user",
    schema: UserRelationSchema.merge(UserExtraFieldsSchema),
    relations: {
        role: { type: "one", entity: () => UserRoleEntityDef },
        exchange_accounts: { type: "many", entity: () => ExchangeAccountEntityDef },
        referral: { type: "one", entity: () => ReferralEntityDef, nullable: true },
        referrals: { type: "many", entity: () => ReferralEntityDef },
        referrer_codes: { type: "many", entity: () => ReferralCodeEntityDef },
        subscription_tier: { type: "one", entity: () => SubscriptionTierEntityDef },
        companies: { type: "many", entity: () => CompanyEntityDef },
        strategies: { type: "many", entity: () => StrategyEntityDef },
        discounts: { type: "many", entity: () => DiscountEntityDef },
        taxProfile: { type: "one", entity: () => TaxProfileEntityDef, nullable: true },
        partnerInfo: { type: "one", entity: () => PartnerInfoEntityDef },
        quizResult: { type: "one", entity: () => QuizResultEntityDef, nullable: true },
        pm_billingData: { type: "one", entity: () => BillingDataEntityDef, nullable: true },
        pm_billingDataAsIssuer: { type: "one", entity: () => BillingDataEntityDef, nullable: true },
        pm_mainPaymentMethod: { type: "one", entity: () => PaymentMethodEntityDef },
        pm_paymentMethods: { type: "many", entity: () => PaymentMethodEntityDef },
        pm_charges: { type: "many", entity: () => ChargeEntityDef },
        pm_payments: { type: "many", entity: () => PaymentEntityDef },
        pm_payouts: { type: "many", entity: () => PayoutEntityDef },
        pm_refunds: { type: "many", entity: () => RefundEntityDef },
        pm_subscriptions: { type: "many", entity: () => SubscriptionEntityDef },
    },
});
// Re-export internal entities
export { CompanyEntityDef, DiscountEntityDef, PartnerInfoEntityDef, QuizResultEntityDef, SubscriptionTierEntityDef, TaxProfileEntityDef, };
